import { React, Fragment, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNoteSticky, faPencil, faTrash, faFolder } from "@fortawesome/free-solid-svg-icons";
import { Menu, Dialog, Transition } from "@headlessui/react";
import {
  Eye,
  MoreVertical,
  Play,
  Link as LinkIcon,
  FileText,
  Star,
  Share2,
  Facebook,
  Instagram,
  Sparkles,
} from "lucide-react";
import "react-avatar";
import moment from "moment";
import axios from "axios";
import { toastify } from "../../components/Toast";
import { useNavigate, Link } from "react-router-dom";
import RichDescription from "../../components/RichDescription/RichDescription";
import { replaceEmojiImagesWithAlt } from "../../components/RichDescription/descriptionSecurity";
import { resolveCtaData } from "./includes/ctaUtils";
import CardCtaSection from "./includes/CardCtaSection";
import CardVideoPlayer from "./includes/CardVideoPlayer";

// Platform icons
import FbIcon from "../../assets/images/fbnewlogo.svg";
import twitterlogo from "../../assets/images/twitternewlogo.svg";
import defaultUser from "../../assets/images/default-user.png";
import youtubeIcon from "../../assets/images/youtube.svg";
import tiktokIcon from "../../assets/images/download-tiktok.png";
import instagramIcon from "../../assets/images/instagram-icon-png.png";
import ZimzeeIcon from "../../assets/icons/logoSymbol.svg";
import FullScreen from "../../assets/icons/FullScreen.svg";
import RegionalScreen from "../../assets/icons/RegionalScreenshot.svg";
import Highlight from "../../assets/icons/HighlightText.svg";


/**
 * Extract plain text from HTML by stripping all tags
 * Used as fallback when OpenAI data is not available
 */
function extractTextFromHtml(html) {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    replaceEmojiImagesWithAlt(doc.body);
    // Remove script/style elements
    doc.querySelectorAll("script, style, svg, canvas").forEach(el => el.remove());
    // Remove "See more" buttons
    doc.querySelectorAll('[role="button"]').forEach(el => {
      if (el.textContent?.trim() === "See more") el.remove();
    });
    let text = doc.body?.textContent || "";
    // Clean up whitespace
    text = text.replace(/\s+/g, " ").trim();

    // Strip Facebook Ads Library UI metadata
    text = text
      .replace(/Active|Inactive|With Issues/gi, (m) => { return ""; })
      .replace(/Library\s*ID:\s*\d+/gi, "")
      .replace(/Started\s+running\s+on\s+\d{1,2}\s+\w+\s+\d{4}/gi, "")
      .replace(/Platforms?/gi, "")
      .replace(/This\s+ad\s+has\s+multiple\s+versions/gi, "")
      .replace(/Open\s+Drop-?down/gi, "")
      .replace(/See\s+ad\s+details/gi, "")
      .replace(/Sponsored/gi, "")
      .replace(/\s+/g, " ")
      .trim();

    return text;
  } catch (e) {
    return "";
  }
}


/**
 * Get the platform icon source for a given card type
 */
function getPlatformIcon(cardType) {
  switch (cardType) {
    case "twitter":
      return twitterlogo;
    case "facebook":
    case "Facebook":
      return FbIcon;
    case "youtube":
      return youtubeIcon;
    case "TikTok":
      return tiktokIcon;
    case "instagram":
      return instagramIcon;
    case "screenshoot":
      return ZimzeeIcon;
    case "fullScreen":
      return FullScreen;
    case "partScreen":
      return RegionalScreen;
    case "highlightText":
      return Highlight;
    default:
      return defaultUser;
  }
}

function NewSingleCard({
  title,
  content,
  note,
  tags,
  updated,
  sharedLength,
  cardId,
  starred,
  cardType,
  handleCheckboxChange,
  cardUpdate,
  buckets,
  postDetail,
  handleSharePop,
  SharedToken,
  PermissionState,
  shared,
  sharedBy,
  PublicBucket = false,
  selectedItems,
  aiData, // Backend extracted data (content.extractedData)
  adData, // existing adData from API
  showCopy = true, // Hide/show copy and CTA
  onStarredChange,
  onTitleChange,
  detailUrl,
}) {
  const [isActive, setIsActive] = useState(starred);
  const [showFullText, setShowFullText] = useState(false);
  const [displayTitle, setDisplayTitle] = useState(title);
  useEffect(() => {
    setDisplayTitle(title);
  }, [title, cardId]);
  let [isOpenss, setIsOpens] = useState(false);
  const [openDuplicate, setOpenDuplicate] = useState(false);
  const [openMove, setOpenMove] = useState(false);
  const [openCopy, setOpenCopy] = useState(false);
  const [optionSelect, setOptionSelect] = useState("");
  const [selectedBucket, setSelectedBucket] = useState("");
  const [searchBucket, setSearchBucket] = useState("");
  const [, setIsBucketOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openCopyLink, setOpenCopyLink] = useState(false);
  const [EditFile, setEditFile] = useState("");
  const [publicUrl, setPublicUrl] = useState("");
  useState("");
  const navigate = useNavigate();
  const [iOS] = useState(
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  );
  const resolveTheme = (val) => {
    if (val === "system" || !val) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return val;
  };
  const [appTheme, setAppTheme] = useState(
    resolveTheme(localStorage.getItem("theme"))
  );

  const actualTheme = appTheme;

  useEffect(() => {
    const handleStorageChange = () => {
      setAppTheme(resolveTheme(localStorage.getItem("theme")));
    };
    window.addEventListener("storage", handleStorageChange);
    setAppTheme(resolveTheme(localStorage.getItem("theme")));

    // Listen for OS theme changes when in "system" mode
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemChange = () => {
      const stored = localStorage.getItem("theme");
      if (stored === "system" || !stored) {
        setAppTheme(mediaQuery.matches ? "dark" : "light");
      }
    };
    mediaQuery.addEventListener("change", handleSystemChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      mediaQuery.removeEventListener("change", handleSystemChange);
    };
  }, []);



  // ----- Derive display data from AI + existing fields -----
  const brandName =
    aiData?.brandName || displayTitle || "Unknown";
  const cardTitle = displayTitle || brandName;
  const brandLogo = postDetail?.imageUrl || (cardType === 'instagram'
    ? getPlatformIcon(cardType)
    : aiData?.brandLogo || getPlatformIcon(cardType));
  // Description fallback chain: AI parsed → adData → extract text from HTML
  let description = aiData?.description
    || adData?.description
    || extractTextFromHtml(content?.html)
    || "";
  const descriptionHtml = aiData?.descriptionHtml || adData?.descriptionHtml || "";
  const descriptionLineHeight = aiData?.descriptionLineHeight || adData?.descriptionLineHeight;
  const hasDescription = Boolean(descriptionHtml?.trim?.() || description);
  // Strip brand name from start of description (it's already shown in card header)
  if (description && brandName && brandName !== "Unknown") {
    const escaped = brandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    description = description.replace(new RegExp(`^\\s*${escaped}\\s*`, 'i'), '').trim();
  }

  // Replace literal '\n' string with actual newline character, and handle \n\n
  if (description) {
    description = description.replace(/\\n/g, '\n');
    // Keep paragraph breaks compact by collapsing consecutive newlines to one.
    description = description.replace(/\n(?:[ \t]*\n)+/g, '\n');
  }






  const primaryMedia = content?.imageUrl?.[0];
  const isFileCard = cardType?.toLowerCase() === "file";
  const fileName = title || primaryMedia?.url?.split("/").pop() || "Untitled file";
  const fileExtension = fileName.includes(".")
    ? fileName.split(".").pop().toUpperCase()
    : "FILE";
  const primaryMediaIsVideo =
    primaryMedia?.urlType?.toLowerCase() === "video" ||
    /\.(mp4|mov|webm)(?:[?#]|$)/i.test(primaryMedia?.url || "");
  let mainImage = primaryMediaIsVideo
    ? primaryMedia?.url
    : primaryMedia?.metaData?.defaultImage || primaryMedia?.url || null;
  let isVideo = Boolean(primaryMediaIsVideo && primaryMedia?.url);
  let videoMedia = primaryMediaIsVideo ? primaryMedia : null;

  // File records store the download URL in imageUrl. It is not preview media,
  // so use a file tile rather than attempting to render the download as <img>.
  if (isFileCard) {
    mainImage = null;
    isVideo = false;
  }
  const hasTextUrlType = Array.isArray(content?.imageUrl)
    ? content.imageUrl.some((img) => img?.urlType?.toLowerCase() === "text")
    : content?.imageUrl?.urlType?.toLowerCase() === "text";

  if (cardType?.toLowerCase()?.includes('instagram') && Array.isArray(content?.imageUrl)) {
    const videoItem = content.imageUrl.find((img) =>
      img?.urlType?.toLowerCase() === "video" ||
      /\.(mp4|mov|webm)(?:[?#]|$)/i.test(img?.url || "")
    );
    if (videoItem?.url) {
      mainImage = videoItem.url;
      isVideo = true;
      videoMedia = videoItem;
    }
  }

  const videoPoster = videoMedia?.metaData?.thumbnail
    || videoMedia?.metaData?.defaultImage
    || (primaryMedia !== videoMedia && primaryMedia?.urlType?.toLowerCase() === "image"
      ? primaryMedia?.metaData?.defaultImage || primaryMedia?.url
      : undefined);

  const backgroundColor = content?.imageUrl?.[0]?.metaData?.backgroundColor || null;
  const isSelected = selectedItems?.includes(cardId);

  const cardDetailLink = detailUrl || (SharedToken && PermissionState
    ? `/app/card-detail/${cardId}?shared=true&permission=${PermissionState}`
    : !PublicBucket
      ? `/app/card-detail/${cardId}`
      : `/public/card/${cardId}`);

  // ----- Handlers (preserved from original SingleCard) -----
  const handleCheckboxChanges = (event) => {
    handleCheckboxChange(event);
  };

  const updateCard = async (cardId, star) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId: cardId, starred: star },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response.data.message);
      if (onStarredChange) {
        onStarredChange(cardId, star);
      } else {
        cardUpdate();
      }
      return true;
    } catch (error) {
      console.error(error);
      toastify("error", error.response.data.message);
      return false;
    }
  };

  const handleStarClick = async () => {
    const nextStarredState = !isActive;
    setIsActive(nextStarredState);
    const updated = await updateCard(cardId, nextStarredState);

    if (!updated) {
      setIsActive(!nextStarredState);
    }
  };

  const handleCardShare = () => {
    handleSharePop(cardId);
  };

  const handleDetailNavigation = (event) => {
    if (!detailUrl) return;
    if (event.target.closest("a, button, input, textarea, select, label, video, iframe")) {
      return;
    }
    navigate(cardDetailLink);
  };

  const handleCardLink = async () => {
    const token = localStorage.getItem("token");
    if (!PublicBucket) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API}cards/public-url`,
          { itemId: cardId, isPublic: true, itemType: "Card" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response?.data?.data) {
          if (iOS) {
            setOpenCopyLink(true);
            setPublicUrl(response?.data?.data);
          } else {
            const tempInput = document.createElement("input");
            tempInput.value = response?.data?.data;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand("copy");
            document.body.removeChild(tempInput);
            toastify("success", response.data.message);
          }
        }
      } catch (error) {
        toastify("error", error?.response?.data?.message);
      }
    } else {
      const tempInput = document.createElement("input");
      tempInput.value = "https://share.zimzee.com/public/card/" + cardId;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
      toastify("success", "Public link is copied to clipboard");
    }
  };

  function closeCardModal() {
    setIsOpens(false);
  }

  const handleDeleteCard = async (Id) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API}cards/delete`,
        { data: { deletedIds: Id }, headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response.data.message);
      cardUpdate();
      closeCardModal();
    } catch (error) {
      console.error(error);
      closeCardModal();
      toastify("error", error.response.data.message);
    }
  };

  const handleUpdateCardMenuName = async (Id) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId: Id, title: EditFile },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response.data.message);
      if (response) {
        setOpenEdit(false);
        setDisplayTitle(EditFile);
        if (onTitleChange) onTitleChange(Id, EditFile);
        else cardUpdate();
      }
    } catch (error) {
      console.error(error);
      toastify("error", error.response.data.message);
    }
  };

  const handleUpdateCardMenu = async (Id) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { optionType: optionSelect, destinationBucketID: selectedBucket, cardId: Id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response.data.message);
      if (response) {
        cardUpdate();
        setOpenCopy(false);
      }
      setSelectedBucket("");
    } catch (error) {
      console.error(error);
      setSelectedBucket("");
      toastify("error", error.response.data.message);
    }
  };

  const bucketNav = [
    { icon: faPencil, name: "Edit Name", href: "#" },
    { icon: faNoteSticky, name: "Move To", href: "#" },
    { icon: faNoteSticky, name: "Copy To", href: "#" },
    { icon: faFolder, name: "Duplicate", href: "#" },
    { icon: faTrash, name: "Delete", href: "" },
  ];

  const handleAction = (item) => {
    if (item.name === "Delete") setIsOpens(true);
    if (item.name === "Duplicate") {
      setOpenDuplicate(true);
      setOptionSelect("Duplicate");
    }
    if (item.name === "Move To") {
      setOpenMove(true);
      setOptionSelect("MoveTo");
    }
    if (item.name === "Copy To") {
      setOpenCopy(true);
      setOptionSelect("CopyTo");
    }
    if (item.name === "Edit Name") {
      setOpenEdit(true);
      setEditFile(displayTitle || "");
    }
  };

  const handleBucketChange = (bucket) => {
    setSelectedBucket(bucket._id);
    setSearchBucket(bucket.name);
    setIsBucketOpen(false);
  };

  const handleSubBucketSelectChange = (subbucket) => {
    setSelectedBucket(subbucket._id);
    setSearchBucket(subbucket.name);
    setIsBucketOpen(false);
  };

  const handleBucketInputChange = (event) => {
    setSearchBucket(event.target.value.toLowerCase());
  };

  const filteredBuckets = buckets?.filter((bucket) => {
    const bucketNameMatches = bucket.name
      .toLowerCase()
      .includes(searchBucket);
    const subBucketMatches = bucket.subBuckets?.some((subBucket) =>
      subBucket.name.toLowerCase().includes(searchBucket)
    );
    return bucketNameMatches || subBucketMatches;
  });

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return "";
    let videoId = "";
    if (url.includes("watch?v=")) {
      videoId = url.split("watch?v=")[1].split("&")[0];
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1].split("?")[0];
    } else if (url.includes("embed/")) {
      return url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  // ----- RENDER -----
  return (
    <div className="group relative break-inside-avoid mb-5 w-full min-w-0 max-w-full" style={{ minWidth: 0, maxWidth: "100%" }}>
      {/* Checkbox — positioned outside card */}
      <div className={`absolute -top-2 -left-2 z-10 transition-all duration-200 ${SharedToken
        ? "hidden"
        : selectedItems?.length > 0
          ? "opacity-100 scale-100"
          : "opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100"
        }`}>
        <label className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center cursor-pointer transition-all shadow-sm ${isSelected
          ? "bg-orange-500 border-orange-500 scale-110"
          : actualTheme === "dark"
            ? "bg-[#111111] border-white/20 hover:border-orange-500"
            : "bg-white border-gray-300 hover:border-orange-500"
          }`}>
          <input
            type="checkbox"
            value={cardId}
            checked={isSelected}
            onChange={handleCheckboxChanges}
            className="sr-only"
          />
          {isSelected && (
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          )}
        </label>
      </div>

      {/* Card */}
      <div onClick={handleDetailNavigation} className={`w-full max-w-full min-w-0 rounded-3xl border overflow-hidden transition-all duration-300 ${detailUrl ? "cursor-pointer" : ""} ${isSelected
        ? "border-orange-500 ring-2 ring-orange-500/30 shadow-lg shadow-orange-500/20 scale-[1.02]"
        : actualTheme === "dark"
          ? "bg-[#111111] border-white/20 hover:bg-white/5 hover:shadow-xl hover:shadow-black/50"
          : "bg-white border-gray-300 hover:bg-gray-50 hover:shadow-xl"
        }`}>

        {/* Card Header — Brand Logo + Name */}
        <div className={`p-2 flex items-center justify-between border-b ${actualTheme === "dark" ? "border-white/10" : "border-gray-200"
          }`}>
          <Link to={cardDetailLink} className="flex items-center gap-2 flex-1 min-w-0 no-underline">
            <img
              src={brandLogo}
              alt="brand"
              className="h-6 w-6 rounded-full object-cover flex-shrink-0 ring-1 ring-white/10"
              onError={(e) => { e.target.src = defaultUser; }}
            />
            <span className={`text-xs font-semibold truncate ${actualTheme === "dark" ? "text-white" : "text-gray-900"
              }`}>
              {cardTitle}
            </span>
            {/* {isSponsored && (
              <span className={`text-xs whitespace-nowrap flex-shrink-0 ${actualTheme === "dark" ? "text-gray-500" : "text-gray-400"
                }`}>
                Sponsored ✓
              </span>
            )} */}
          </Link>
          <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Link
              to={cardDetailLink}
              className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${actualTheme === "dark"
                ? "text-gray-400 hover:text-white hover:bg-white/5"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
            >
              <Eye className="h-3.5 w-3.5" />
            </Link>
            {!PublicBucket && (
              <Menu as="div" className="relative">
                <Menu.Button aria-label="Card actions" className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${actualTheme === "dark"
                  ? "text-gray-400 hover:text-white hover:bg-white/5"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}>
                  <MoreVertical className="h-3.5 w-3.5" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className={`absolute text-left right-0 z-10 mt-2 w-48 origin-top-right rounded-xl py-1 shadow-lg ring-1 focus:outline-none ${actualTheme === "dark"
                    ? "bg-[#1a1a1a] ring-white/10"
                    : "bg-white ring-black/5"
                    }`}>
                    {bucketNav.map((item) => (
                      <Menu.Item key={item.name}>
                        {({ active }) => (
                          <button type="button" data-former-link=""

                            className={classNames(
                              active
                                ? actualTheme === "dark" ? "bg-white/5" : "bg-gray-100"
                                : "",
                              `block py-2 px-4 text-sm ${actualTheme === "dark" ? "text-gray-300" : "text-gray-700"}`
                            )}
                            onClick={() => handleAction(item)}
                          >
                            <span className="pr-2">
                              <FontAwesomeIcon
                                className={actualTheme === "dark" ? "text-gray-500" : "text-[#797979]"}
                                icon={item.icon}
                              />
                            </span>
                            <span>{item.name}</span>
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
          </div>
        </div>

        {/* Ad Copy / Description */}
        {showCopy && !backgroundColor && hasDescription && (
          <Link to={cardDetailLink} className="block no-underline">
            <div className={`px-2 py-2 border-b ${actualTheme === "dark" ? "border-white/10 hover:bg-white/5" : "border-gray-200 hover:bg-gray-50"} transition-colors`}>
              <RichDescription
                descriptionHtml={descriptionHtml}
                description={description}
                descriptionLineHeight={descriptionLineHeight}
                maxWords={20}
                expanded={showFullText}
                onToggle={setShowFullText}
                onToggleClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                className={`text-sm leading-snug overflow-hidden ${actualTheme === "dark" ? "text-gray-300" : "text-gray-700"}`}
                toggleClassName={`font-medium ${actualTheme === "dark" ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900"}`}
              />
            </div>
          </Link>
        )}

        {/* File preview */}
        {isFileCard && (
          <Link to={cardDetailLink} className="block no-underline">
            <div className={`min-h-[174px] px-5 py-6 flex flex-col items-center justify-center gap-3 border-b transition-colors ${actualTheme === "dark"
              ? "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
              : "border-gray-200 bg-gray-50 hover:bg-gray-100"
              }`}>
              <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${actualTheme === "dark" ? "bg-orange-500/15 text-orange-400" : "bg-orange-100 text-orange-600"}`}>
                <FileText className="h-7 w-7" />
              </div>
              <div className="min-w-0 max-w-full text-center">
                <p className={`m-0 truncate text-sm font-semibold ${actualTheme === "dark" ? "text-white" : "text-gray-900"}`}>
                  {fileName}
                </p>
                <span className={`mt-1 inline-block rounded-full px-2.5 py-1 text-[10px] font-bold tracking-wide ${actualTheme === "dark" ? "bg-white/10 text-gray-300" : "bg-gray-200 text-gray-600"}`}>
                  {fileExtension} FILE
                </span>
              </div>
            </div>
          </Link>
        )}

        {/* Card Image */}
        {/* Card Media — Video (plays inline, not wrapped in link) */}
        {mainImage && isVideo && (
          <div className="relative w-full overflow-hidden bg-[#111111]">
            <CardVideoPlayer key={mainImage} src={mainImage} poster={videoPoster} title={title} />
            {/* Overlay buttons on video hover */}
            <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCardLink();
                }}
                className={`h-8 w-8 rounded-xl backdrop-blur-md shadow-lg flex items-center justify-center transition-colors ${actualTheme === "dark"
                  ? "bg-[#111111]/90 hover:bg-[#111111] text-white border border-white/10"
                  : "bg-white/90 hover:bg-white text-gray-900 border border-gray-200"
                  }`}
              >
                <LinkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Card Media — Image (wrapped in link to detail page) */}
        {mainImage && !isVideo && (
          <Link to={cardDetailLink} className="card-media-link block w-full max-w-full overflow-hidden">
            <div className={`card-media-preview relative overflow-hidden w-full max-w-full ${(cardType === "partScreen" || cardType === "fullScreen")
              ? "aspect-[9/16] overflow-y-auto screenshot-scrollbar"
              : ""
              }`}>
              {backgroundColor ? (
                <div
                  className="w-full aspect-square flex items-center justify-center p-6 text-center text-white"
                  style={{ backgroundColor }}
                >
                  <RichDescription
                    descriptionHtml={descriptionHtml}
                    description={description}
                    descriptionLineHeight={descriptionLineHeight}
                    className="text-xl font-semibold break-words"
                  />
                </div>
              ) : (
                <img
                  src={mainImage}
                  alt={title}
                  className="w-full h-auto block"
                />
              )}
              {/* Overlay buttons on image hover */}
              <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCardLink();
                  }}
                  className={`h-8 w-8 rounded-xl backdrop-blur-md shadow-lg flex items-center justify-center transition-colors ${actualTheme === "dark"
                    ? "bg-[#111111]/90 hover:bg-[#111111] text-white border border-white/10"
                    : "bg-white/90 hover:bg-white text-gray-900 border border-gray-200"
                    }`}
                >
                  <LinkIcon className="h-4 w-4" />
                </button>
                {!hasTextUrlType && (
                  <a
                    href={`/app/ads?referenceId=${cardId}`}
                    onClick={(e) => e.stopPropagation()}
                    className={`h-8 w-8 rounded-xl backdrop-blur-md shadow-lg flex items-center justify-center transition-colors ${actualTheme === "dark"
                      ? "bg-[#111111]/90 hover:bg-[#111111] border border-white/10"
                      : "bg-white/90 hover:bg-white border border-gray-200"
                      }`}
                  >
                    <Sparkles className="h-4 w-4 text-amber-400" />
                  </a>
                )}
              </div>
            </div>
          </Link>
        )}

        {/* Card Media — Youtube Iframe Fallback */}
        {cardType?.toLowerCase() === 'youtube' && !mainImage && content?.sourceUrl && (
          <div className="relative w-full aspect-video overflow-hidden">
            <iframe
              className="w-full h-full"
              src={getYoutubeEmbedUrl(content.sourceUrl)}
              title={title || "YouTube video player"}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        )}

        {/* CTA Section — via shared resolveCtaData + CardCtaSection */}
        {showCopy && (
          <CardCtaSection
            cta={resolveCtaData({ content, aiData, adData, title })}
            variant="card"
            actualTheme={actualTheme}
          />
        )}

        {/* Card Footer */}
        <div className="px-2 py-2 space-y-2">
          {/* Last Updated */}
          <p className={`text-[10px] ${actualTheme === "dark" ? "text-gray-500" : "text-gray-400"}`}>
            Last updated on {moment(updated).format("MMM D, YYYY @ hh:mm A")}
          </p>

          {/* Tags */}
          <div className="flex items-center justify-between">
            {tags && tags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className={`text-xs px-2.5 py-1 rounded-lg font-medium ${actualTheme === "dark"
                      ? "bg-white/5 text-gray-300 hover:bg-white/10"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                  >
                    {tag.name || tag}
                  </span>
                ))}
              </div>
            ) : (
              <span className={`text-xs ${actualTheme === "dark" ? "text-gray-500" : "text-gray-400"}`}>
                No tags assigned
              </span>
            )}
          </div>

          {/* Source Platform Icon + Action Buttons */}
          <div className="flex items-center justify-between pt-2">
            {/* Social Platform Icon */}
            <div className="flex items-center">
              {(cardType === "facebook" || cardType === "Facebook") && (
                <div className="h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center shadow-sm">
                  <Facebook className="h-3 w-3 text-white" fill="white" />
                </div>
              )}
              {cardType === "instagram" && (
                <div className="h-5 w-5 rounded-full bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 flex items-center justify-center shadow-sm">
                  <Instagram className="h-3 w-3 text-white" />
                </div>
              )}
              {cardType === "twitter" && (
                <div className="h-5 w-5 rounded-full bg-black flex items-center justify-center shadow-sm">
                  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="white">
                    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
                  </svg>
                </div>
              )}
              {cardType === "TikTok" && (
                <div className="h-5 w-5 rounded-full bg-black flex items-center justify-center shadow-sm">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="white">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                  </svg>
                </div>
              )}
              {cardType === "youtube" && (
                <div className="h-5 w-5 rounded-full bg-red-600 flex items-center justify-center shadow-sm">
                  <Play className="h-3 w-3 text-white" fill="white" />
                </div>
              )}
              {!["facebook", "Facebook", "instagram", "twitter", "TikTok", "youtube"].includes(cardType) && (
                <div className={`h-5 w-5 rounded-full flex items-center justify-center shadow-sm ${actualTheme === "dark" ? "bg-white/10" : "bg-gray-200"
                  }`}>
                  <img src={getPlatformIcon(cardType)} alt={cardType} className="h-3.5 w-3.5" />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-1">
              {/* Notes */}
              <a
                href={cardDetailLink}
                className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all duration-200 ${note
                  ? "text-orange-500 hover:text-orange-600"
                  : actualTheme === "dark"
                    ? "text-gray-400 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
              >
                <FileText className="h-4 w-4" />
              </a>
              {/* Star */}
              <button
                onClick={handleStarClick}
                className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all duration-200 ${isActive
                  ? "text-yellow-400 hover:text-yellow-300"
                  : actualTheme === "dark"
                    ? "text-gray-400 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
              >
                <Star className="h-4 w-4" fill={isActive ? "currentColor" : "none"} />
              </button>
              {/* Share */}
              <button
                onClick={handleCardShare}
                className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all duration-200 ${actualTheme === "dark"
                  ? "text-gray-400 hover:text-white hover:bg-white/5"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
              >
                <Share2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== MODALS (Delete, Edit, Move, Copy, Duplicate, CopyLink) ===== */}

      {/* Delete Confirmation Modal */}
      <Transition appear show={isOpenss} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeCardModal}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all text-center">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Delete</Dialog.Title>
                  <div className="mt-2"><p className="text-sm text-gray-500">Are you sure you want to delete?</p></div>
                  <div className="mt-4">
                    <button style={{ background: "#FF6600", width: "80px", color: "#fff", padding: "10px", borderRadius: "50px" }} type="button" className="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium" onClick={() => handleDeleteCard(cardId)}>Yes</button>
                    <button style={{ background: "#000", width: "80px", color: "#fff", padding: "10px", borderRadius: "50px" }} type="button" className="ml-4 inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium" onClick={closeCardModal}>No</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Edit Name Modal */}
      <Transition appear show={openEdit} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setOpenEdit(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Edit Name</Dialog.Title>
                  <div className="mt-4"><input type="text" value={EditFile} onChange={(e) => setEditFile(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button style={{ background: "#000", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => setOpenEdit(false)}>Cancel</button>
                    <button style={{ background: "#FF6600", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => handleUpdateCardMenuName(cardId)}>Save</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Duplicate Confirmation Modal */}
      <Transition appear show={openDuplicate} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setOpenDuplicate(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all text-center">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Duplicate</Dialog.Title>
                  <div className="mt-2"><p className="text-sm text-gray-500">Are you sure you want to duplicate?</p></div>
                  <div className="mt-4">
                    <button style={{ background: "#FF6600", width: "80px", color: "#fff", padding: "10px", borderRadius: "50px" }} onClick={() => handleUpdateCardMenu(cardId)}>Yes</button>
                    <button style={{ background: "#000", width: "80px", color: "#fff", padding: "10px", borderRadius: "50px" }} className="ml-4" onClick={() => setOpenDuplicate(false)}>No</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Move To Modal */}
      <Transition appear show={openMove} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => { setOpenMove(false); setSearchBucket(""); }}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full min-w-0 max-w-md max-h-[calc(100dvh-2rem)] transform overflow-y-auto rounded-2xl bg-white p-6 text-left text-gray-900 align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Move To</Dialog.Title>
                  <div className="mt-4">
                    <div className="min-w-0">
                      <input type="text" placeholder="Select a bucket" value={searchBucket} onChange={handleBucketInputChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      <div className="mt-2 max-h-[min(200px,40vh)] overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-gray-50">
                        <ul className="m-0 list-none p-1">
                          {filteredBuckets?.map((option) => (
                            <li key={option._id} className="py-1">
                              <button type="button" className="w-full whitespace-normal break-words text-left hover:bg-gray-100 px-2 py-1 rounded text-sm" onClick={() => handleBucketChange(option)}>{option.name}</button>
                              {option.subBuckets?.length > 0 && (
                                <ul className="pl-4">
                                  {option.subBuckets.map((sub) => (
                                    <li key={sub._id}><button type="button" className="w-full whitespace-normal break-words text-left hover:bg-gray-100 px-2 py-1 rounded text-sm" onClick={() => handleSubBucketSelectChange(sub)}>-- {sub.name}</button></li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <button style={{ background: "#000", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => { setOpenMove(false); setSearchBucket(""); }}>Cancel</button>
                      <button style={{ background: "#FF6600", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => handleUpdateCardMenu(cardId)}>Move</button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Copy To Modal */}
      <Transition appear show={openCopy} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => { setOpenCopy(false); setSearchBucket(""); }}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full min-w-0 max-w-md max-h-[calc(100dvh-2rem)] transform overflow-y-auto rounded-2xl bg-white p-6 text-left text-gray-900 align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Copy To</Dialog.Title>
                  <div className="mt-4">
                    <div className="min-w-0">
                      <input type="text" placeholder="Select a bucket" value={searchBucket} onChange={handleBucketInputChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      <div className="mt-2 max-h-[min(200px,40vh)] overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-gray-50">
                        <ul className="m-0 list-none p-1">
                          {filteredBuckets?.map((option) => (
                            <li key={option._id} className="py-1">
                              <button type="button" className="w-full whitespace-normal break-words text-left hover:bg-gray-100 px-2 py-1 rounded text-sm" onClick={() => handleBucketChange(option)}>{option.name}</button>
                              {option.subBuckets?.length > 0 && (
                                <ul className="pl-4">
                                  {option.subBuckets.map((sub) => (
                                    <li key={sub._id}><button type="button" className="w-full whitespace-normal break-words text-left hover:bg-gray-100 px-2 py-1 rounded text-sm" onClick={() => handleSubBucketSelectChange(sub)}>-- {sub.name}</button></li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <button style={{ background: "#000", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => { setOpenCopy(false); setSearchBucket(""); }}>Cancel</button>
                      <button style={{ background: "#FF6600", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => handleUpdateCardMenu(cardId)}>Copy</button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Copy Link Modal (iOS) */}
      <Transition appear show={openCopyLink} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setOpenCopyLink(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">Public Link</Dialog.Title>
                  <div className="mt-4"><input type="text" value={publicUrl} readOnly className="publicUrlInput w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div className="mt-4 flex justify-end">
                    <button style={{ background: "#FF6600", color: "#fff", padding: "8px 20px", borderRadius: "50px" }} onClick={() => {
                      const el = document.querySelector('.publicUrlInput');
                      el.select();
                      document.execCommand('copy');
                      toastify("success", "Public link is copied to clipboard");
                      setOpenCopyLink(false);
                    }}>Copy Link</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default NewSingleCard;
